/**
 * {@link createQueryKeyFactories} gives us a standard method of creating basic query key factories
 * for a particular resource type.  These can be used with react-query and standardizes our
 * query keys, making it easier to interact with the query cache from different locations.
 *
 * See {@link https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories} for the
 * inspiration to this approach.
 *
 * @param baseKey The resource name, e.g. 'hosts', or 'networks'
 * @returns An object containing queryKey factories like all() and lists()
 */
function createQueryKeyFactories(baseKey: string) {
  const all = () => [baseKey];
  const lists = () => [...all(), 'list'] as const;
  const list = (
    cursor?: string,
    pageSize?: number,
    ...rest: Array<string | number | Record<string, string | number | boolean | string[]> | undefined>
    // eslint-disable-next-line @typescript-eslint/no-misused-spread -- We don't care about the keys, 0, 1, 2 is fine, we just want stable key/value pairs
  ) => [...lists(), { cursor, pageSize, ...rest }] as const;
  const details = () => [...all(), 'detail'] as const;
  const detail = (id: string) => [...details(), id] as const;
  const maxList = list(undefined, Number.POSITIVE_INFINITY);
  return { all, lists, list, details, detail, maxList };
}

export const hostQueryKeys = createQueryKeyFactories('hosts');

export const networkQueryKeys = createQueryKeyFactories('networks');

export const managedLighthouseQueryKeys = createQueryKeyFactories('managedLighthouses');

export const roleQueryKeys = {
  ...createQueryKeyFactories('roles'),
  roleName: (roleId: string) => [...createQueryKeyFactories('roles').details(), 'roleName', roleId],
};

export const tagQueryKeys = createQueryKeyFactories('tags');

export const userQueryKeys = {
  all: () => ['users'],
  anyCurrent: () => [...userQueryKeys.all(), 'current'],
  current: ({ userToken, oidcToken }: { userToken?: string; oidcToken?: string }) => [
    ...userQueryKeys.anyCurrent(),
    { userToken, oidcToken },
  ],
};

export const oidcProviderQueryKeys = createQueryKeyFactories('oidcProviders');

export const endpointOidcProviderQueryKeys = createQueryKeyFactories('endpointOidcProviders');

export const endpointOidcUserQueryKeys = createQueryKeyFactories('endpointOidcUsers');

export const googleIdPQueryKeys = {
  all: () => ['googleIdP'],
  connection: () => [...googleIdPQueryKeys.all(), 'connection'],
  providers: () => [...googleIdPQueryKeys.all(), 'providers'],
};

export const orgQueryKeys = {
  all: () => ['org'],
  featureFlags: () => [...orgQueryKeys.all(), 'featureFlags'],
  deploymentFeatureFlags: () => [...orgQueryKeys.all(), 'deploymentFeatureFlags'],
};

export const totpAuthenticatorQueryKeys = createQueryKeyFactories('totpAuthenticators');

export const webAuthnAuthenticatorQueryKeys = createQueryKeyFactories('webAuthnAuthenticators');

export const auditLogQueryKeys = {
  ...createQueryKeyFactories('auditLogs'),
  csv: ({ targetType, timeFrame, targetId }: { targetType?: string; timeFrame?: string; targetId?: string }) =>
    ['auditLogs', 'csv', { targetType, timeFrame, targetId }] as const,
};

export const adminQueryKeys = createQueryKeyFactories('admins');

export const apiKeyQueryKeys = createQueryKeyFactories('apiKeys');

export const joinKeyQueryKeys = createQueryKeyFactories('joinKeys');

export const enrollmentCodeQueryKeys = {
  forHost: (hostId: string) => ['enrollment-code', hostId],
  check: (code: string) => ['enrollment-code', 'check', code],
};

export const certificateAuthorityQueryKeys = createQueryKeyFactories('certificateAuthorities');

export const billingQueryKeys = {
  billingDetails: ['billing', 'billingDetails'],
};

export const routeQueryKeys = createQueryKeyFactories('routes');

export const domainQueryKeys = createQueryKeyFactories('domains');
